// src/utils/VisualizationUtils.js
import React from 'react';
import { Bar, Pie, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { PARTY_COLORS } from '../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Generates a table based on the provided data.
 * @param {Array} data - The data to display in the table.
 * @returns {JSX.Element} - A table component.
 */
export const generateTable = (data) => {
  if (!data || data.length === 0) {
    return <p>No data available to display.</p>;
  }

  // Get all unique keys from all items
  const headers = [...new Set(data.flatMap(item => Object.keys(item)))];
  
  return (
    <table>
      <thead>
        <tr>
          {headers.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            {headers.map((header) => (
              <td key={header}>{item[header] || 'N/A'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Get colors based on the party name
 * @param {Array} data - The data array
 * @param {string} partyKey - The key used to identify party in the data
 * @returns {Object} - Background and border colors
 */
const getPartyColors = (data, partyKey) => {
  const backgroundColors = [];
  const borderColors = [];
  
  data.forEach(item => {
    const party = item[partyKey];
    const colorScheme = PARTY_COLORS[party] || PARTY_COLORS.default;
    backgroundColors.push(colorScheme.backgroundColor);
    borderColors.push(colorScheme.borderColor);
  });
  
  return {
    backgroundColor: backgroundColors,
    borderColor: borderColors
  };
};

/**
 * Generates a bar chart based on the provided data.
 * @param {Array} data - The data to display in the bar chart.
 * @param {string} labelKey - The key to use for labels.
 * @param {string} dataKey - The key to use for data values.
 * @param {Object} options - Additional options like partyKey.
 * @returns {JSX.Element} - A bar chart component.
 */
export const generateBarChart = (data, labelKey, dataKey, options = {}) => {
  if (!data || data.length === 0) {
    return <p>No data available for bar chart.</p>;
  }

  const safeData = Array.isArray(data) ? data : [];
  const labels = safeData.map((item) => item[labelKey] || 'Unknown');
  const values = safeData.map((item) => Number(item[dataKey]) || 0);
  
  // Get colors based on party if partyKey is provided
  let colors = {
    backgroundColor: 'rgba(54, 162, 235, 0.6)',
    borderColor: 'rgba(54, 162, 235, 1)'
  };
  
  if (options.partyKey && safeData.some(item => item[options.partyKey])) {
    colors = getPartyColors(safeData, options.partyKey);
  }

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: dataKey.charAt(0).toUpperCase() + dataKey.slice(1),
        data: values,
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `${dataKey.charAt(0).toUpperCase() + dataKey.slice(1)} by ${labelKey.charAt(0).toUpperCase() + labelKey.slice(1)}`,
      },
    },
  };

  return (
    <div className="viz">
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
};

/**
 * Generates a pie chart based on the provided data.
 * @param {Array} data - The data to display in the pie chart.
 * @param {string} labelKey - The key to use for labels.
 * @param {string} dataKey - The key to use for data values.
 * @param {Object} options - Additional options like partyKey.
 * @returns {JSX.Element} - A pie chart component.
 */
export const generatePieChart = (data, labelKey, dataKey, options = {}) => {
  if (!data || data.length === 0) {
    return <p>No data available for pie chart.</p>;
  }

  const safeData = Array.isArray(data) ? data : [];
  const labels = safeData.map((item) => item[labelKey] || 'Unknown');
  const values = safeData.map((item) => Number(item[dataKey]) || 0);
  
  // Get colors based on party if partyKey is provided
  let colors = {
    backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(75, 192, 192, 0.6)'],
    borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(75, 192, 192, 1)']
  };
  
  if (options.partyKey && safeData.some(item => item[options.partyKey])) {
    colors = getPartyColors(safeData, options.partyKey);
  }

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: dataKey.charAt(0).toUpperCase() + dataKey.slice(1),
        data: values,
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: `${dataKey.charAt(0).toUpperCase() + dataKey.slice(1)} by ${labelKey.charAt(0).toUpperCase() + labelKey.slice(1)}`,
      },
    },
  };

  return (
    <div className="viz">
      <Pie data={chartData} options={chartOptions} />
    </div>
  );
};

/**
 * Generates a histogram based on vote data.
 * @param {Array} data - The data to display in the histogram.
 * @param {string} dataKey - The key to use for data values.
 * @returns {JSX.Element} - A histogram component (using Bar chart).
 */
export const generateHistogram = (data, dataKey) => {
  if (!data || data.length === 0) {
    return <p>No data available for histogram.</p>;
  }

  // Get all values 
  const values = data.map(item => Number(item[dataKey]) || 0);
  
  // Create buckets for the histogram
  const min = Math.min(...values);
  const max = Math.max(...values);
  const bucketSize = (max - min) / 10;
  const buckets = Array(10).fill(0);
  
  values.forEach(value => {
    const bucketIndex = Math.min(Math.floor((value - min) / bucketSize), 9);
    buckets[bucketIndex]++;
  });
  
  // Create labels for each bucket
  const labels = buckets.map((_, index) => {
    const lowerBound = min + index * bucketSize;
    const upperBound = min + (index + 1) * bucketSize;
    return `${lowerBound.toFixed(0)}-${upperBound.toFixed(0)}`;
  });
  
  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Frequency',
        data: buckets,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Distribution of ${dataKey.charAt(0).toUpperCase() + dataKey.slice(1)}`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Frequency'
        }
      },
      x: {
        title: {
          display: true,
          text: dataKey.charAt(0).toUpperCase() + dataKey.slice(1)
        }
      }
    }
  };
  
  return (
    <div className="viz">
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Generates a scatter plot based on the provided data.
 * @param {Array} data - The data to display in the scatter plot.
 * @param {string} xKey - The key to use for x-axis values.
 * @param {string} yKey - The key to use for y-axis values.
 * @param {Object} options - Additional options like partyKey.
 * @returns {JSX.Element} - A scatter plot component.
 */
export const generateScatterPlot = (data, xKey, yKey, options = {}) => {
  if (!data || data.length === 0) {
    return <p>No data available for scatter plot.</p>;
  }

  const safeData = Array.isArray(data) ? data : [];
  const scatterData = safeData.map(item => ({
    x: Number(item[xKey]) || 0,
    y: Number(item[yKey]) || 0
  }));
  
  // Group by party if partyKey is provided
  let datasets = [{
    label: `${xKey} vs ${yKey}`,
    data: scatterData,
    backgroundColor: 'rgba(54, 162, 235, 0.6)',
    borderColor: 'rgba(54, 162, 235, 1)',
  }];
  
  if (options.partyKey && safeData.some(item => item[options.partyKey])) {
    // Group data by party
    const parties = [...new Set(safeData.map(item => item[options.partyKey] || 'Unknown'))];
    
    datasets = parties.map(party => {
      const partyData = safeData
        .filter(item => (item[options.partyKey] || 'Unknown') === party)
        .map(item => ({
          x: Number(item[xKey]) || 0,
          y: Number(item[yKey]) || 0
        }));
      
      const colorScheme = PARTY_COLORS[party] || PARTY_COLORS.default;
      
      return {
        label: party,
        data: partyData,
        backgroundColor: colorScheme.backgroundColor,
        borderColor: colorScheme.borderColor,
      };
    });
  }
  
  const chartData = {
    datasets: datasets
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `${yKey} vs ${xKey}`,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: xKey.charAt(0).toUpperCase() + xKey.slice(1)
        }
      },
      y: {
        title: {
          display: true,
          text: yKey.charAt(0).toUpperCase() + yKey.slice(1)
        }
      }
    }
  };
  
  return (
    <div className="viz">
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
};

/**
 * Generate the appropriate visualization based on type
 * @param {Array} data - The data to visualize
 * @param {string} type - The visualization type (table, bar, pie, etc.)
 * @param {string} labelKey - The key for labels
 * @param {string} dataKey - The key for data values
 * @param {Object} options - Additional options
 * @returns {JSX.Element} - The visualization component
 */
export const generateVisualization = (data, type, labelKey, dataKey, options = {}) => {
  switch (type) {
    case 'table':
      return generateTable(data);
    case 'bar':
      return generateBarChart(data, labelKey, dataKey, options);
    case 'pie':
      return generatePieChart(data, labelKey, dataKey, options);
    case 'histogram':
      return generateHistogram(data, dataKey);
    case 'scatterplot':
      return generateScatterPlot(data, labelKey, dataKey, options);
    default:
      return generateTable(data);
  }
};